import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Undo2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'

import { ComposeTextarea } from '@/components/compose-field'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { newRequestId } from '@/features/quote-builder/publishing/api'
import { formatMoney } from '@/lib/utils'

import {
  newInvoiceErrorMessage,
  receivablesApi,
  refundOptionsQueryOptions,
  useReceivablesApi,
  type RefundOption,
  type RefundSource,
} from './api'
import { blankLine, lineCents, lineValid, type Line } from './invoice-lines'
import { InvoiceLinesEditor } from './invoice-lines-editor'

function actionLabel(option: RefundOption | undefined, cents: number) {
  const amount = formatMoney(cents / 100)
  if (option?.kind === 'credit') return `Take ${amount} off`
  if (option?.kind === 'refund_outside') return `Record ${amount} refund`
  return `Refund ${amount}`
}

/**
 * Take items off an invoice (USA-378): a paid invoice is refunded the way the customer paid, one
 * paid by check or wire is recorded for staff to pay back, and an unpaid one owes less.
 */
export function RefundDialog({ source, trigger }: { source: RefundSource; trigger?: ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" variant="outline">
            <Undo2 aria-hidden="true" />
            Refund
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        {open ? <RefundForm onDone={() => setOpen(false)} source={source} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function RefundForm({ source, onDone }: { source: RefundSource; onDone: () => void }) {
  const api = useReceivablesApi()
  const client = useQueryClient()
  const options = useQuery(refundOptionsQueryOptions(api, source))
  const [invoiceId, setInvoiceId] = useState<string | null>(null)
  const [lines, setLines] = useState<Line[]>(() => [blankLine()])
  const [memo, setMemo] = useState('')
  const [submitted, setSubmitted] = useState(false)
  // One id per refund: a retry after an error finishes the same credit note instead of a second.
  const [requestId] = useState(newRequestId)

  const data = options.data
  const invoices = data?.invoices ?? []
  const option = invoices.find((item) => item.invoice_id === invoiceId) ?? invoices[0]
  const totalCents = lines.reduce((sum, line) => sum + (lineCents(line) ?? 0), 0)
  const availableCents = option ? Math.round(Number(option.available) * 100) : 0
  const errors = {
    lines: lines.some((line) => !lineValid(line, { positive: true })),
    total: totalCents <= 0 || totalCents > availableCents,
  }

  const refund = useMutation({
    retry: false,
    mutationFn: () =>
      receivablesApi.refund(api, {
        request_id: requestId,
        invoice_id: option!.invoice_id,
        lines: lines.map((line) => ({
          description: line.description.trim(),
          quantity: line.quantity.trim(),
          unit_price: line.unitPrice.trim(),
        })),
        memo: memo.trim() || null,
      }),
    onSuccess: (invoice) => {
      void client.invalidateQueries({ queryKey: ['receivables'] })
      void client.invalidateQueries({ queryKey: ['projects', 'payments'] })
      void client.invalidateQueries({ queryKey: ['quote-invoicing'] })
      toast.success(
        option?.kind === 'credit'
          ? `${formatMoney(totalCents / 100)} taken off invoice ${invoice.invoice_number}`
          : `${formatMoney(totalCents / 100)} refunded on invoice ${invoice.invoice_number}`,
      )
      onDone()
    },
  })
  const submit = () => {
    setSubmitted(true)
    if (option && !errors.lines && !errors.total && !refund.isPending) refund.mutate()
  }

  return (
    <form
      className="grid gap-5"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <DialogHeader>
        <DialogTitle>Refund</DialogTitle>
        <p className="flex min-h-5 flex-wrap items-center justify-center gap-x-2 text-sm text-muted-foreground sm:justify-start">
          {data ? (
            <>
              <span className="font-medium text-foreground">{data.customer_name}</span>
              {data.project ? <span>· {data.project.name}</span> : null}
            </>
          ) : options.isError ? (
            <span className="text-destructive">The invoices could not be loaded. Close and try again.</span>
          ) : (
            'Loading invoices…'
          )}
        </p>
      </DialogHeader>

      {data && !invoices.length ? (
        <p className="text-sm text-muted-foreground">No invoice has anything left to refund.</p>
      ) : null}

      {invoices.length ? (
        <div className="grid gap-1.5">
          <span className="text-sm font-medium">From invoice</span>
          <ReviewChoiceGroup
            label="From invoice"
            onChange={setInvoiceId}
            options={invoices.map((item) => ({
              value: item.invoice_id,
              label: `${item.invoice_number} · ${item.status === 'paid' ? 'paid' : 'unpaid'} · up to ${formatMoney(item.available)}`,
            }))}
            value={option?.invoice_id ?? null}
          />
          {option?.kind === 'refund_outside' ? (
            <p className="text-xs text-muted-foreground">Paid by check or wire: send this refund to the customer.</p>
          ) : null}
        </div>
      ) : null}

      {invoices.length ? (
        <div className="grid gap-2">
          <InvoiceLinesEditor lines={lines} onChange={setLines} poItems={data?.po_items ?? []} positive submitted={submitted} />
          {submitted && (errors.lines || errors.total) ? (
            <p className="text-xs text-destructive">
              {errors.lines
                ? 'Every item needs a description, a quantity and a price.'
                : `The refund must be more than zero and at most ${formatMoney(availableCents / 100)}.`}
            </p>
          ) : null}
        </div>
      ) : null}

      {invoices.length ? (
        <div className="grid gap-1.5">
          <Label htmlFor="refund-memo">Reason</Label>
          <ComposeTextarea
            className="min-h-9"
            id="refund-memo"
            maxLength={1000}
            onChange={(event) => setMemo(event.target.value)}
            placeholder="Optional"
            rows={1}
            value={memo}
          />
        </div>
      ) : null}

      {refund.isError ? (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert">
          {newInvoiceErrorMessage(refund.error)}
        </p>
      ) : null}

      <DialogFooter className="gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
        <Button onClick={onDone} type="button" variant="outline">
          Cancel
        </Button>
        {invoices.length ? (
          <LoadingButton loading={refund.isPending} loadingText="Refunding..." type="submit">
            <Undo2 aria-hidden="true" />
            {actionLabel(option, totalCents)}
          </LoadingButton>
        ) : null}
      </DialogFooter>
    </form>
  )
}
